import React, { useRef, useState } from "react";
import { X } from "lucide-react";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { LinkedCampaignOption } from "../hooks/useLinkedCampaignSearch";

interface LinkedCampaignComboboxProps {
  query: string;
  onQueryChange: (q: string) => void;
  results: LinkedCampaignOption[];
  isLoading: boolean;
  selectedId: number | null | undefined;
  selectedName: string | null | undefined;
  onSelect: (id: number | null, name: string | null) => void;
  error?: string;
  disabled?: boolean;
}

const formatDate = (iso: string) => {
  if (!iso) return "";
  const [year, month, day] = iso.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Ativa",
  INACTIVE: "Inativa",
  SCHEDULED: "Agendada",
  FINISHED: "Encerrada",
};

export const LinkedCampaignCombobox: React.FC<LinkedCampaignComboboxProps> = ({
  query,
  onQueryChange,
  results,
  isLoading,
  selectedId,
  selectedName,
  onSelect,
  error,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQueryChange(e.target.value);
    setOpen(true);
  };

  const handleSelect = (option: LinkedCampaignOption) => {
    onSelect(option.id, option.campaignName);
    onQueryChange("");
    setOpen(false);
  };

  const handleClear = () => {
    onSelect(null, null);
    onQueryChange("");
    setOpen(false);
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative" onBlur={handleBlur}>
      <Label htmlFor="linkedCampaign">
        Campanha Vinculada{" "}
        <span className="text-xs font-normal text-muted-foreground">
          (opcional)
        </span>
      </Label>
      <p className="text-xs text-muted-foreground mb-1">
        Opcional. Selecione uma campanha "Chute ao Gol" encerrada para acumular
        vouchers. Deixe em branco para uma campanha sem vínculo.
      </p>

      {selectedId ? (
        <div className={`flex items-center gap-2 px-3 py-2 border rounded-md bg-muted ${disabled ? "opacity-60" : ""}`}>
          <span className="flex-1 text-sm truncate">{selectedName}</span>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Remover campanha vinculada"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <Input
            id="linkedCampaign"
            placeholder="Buscar campanha anterior encerrada..."
            value={query}
            onChange={handleInputChange}
            onFocus={() => !disabled && setOpen(true)}
            autoComplete="off"
            disabled={disabled}
          />
          {open && (
            <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-auto">
              {isLoading && (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  Buscando...
                </div>
              )}
              {!isLoading && results.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  {query.length > 0 ? "Nenhuma campanha encontrada" : "Digite para buscar campanhas encerradas"}
                </div>
              )}
              {!isLoading &&
                results.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent focus:bg-accent outline-none"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(option);
                    }}
                  >
                    <span className="font-medium">{option.campaignName}</span>
                    {option.status && (
                      <span className="ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium bg-muted text-muted-foreground">
                        {STATUS_LABELS[option.status] ?? option.status}
                      </span>
                    )}
                    <span className="ml-2 text-xs text-muted-foreground">
                      {formatDate(option.startDate)} — {formatDate(option.endDate)}
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="text-sm text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
};
